import {GroupedOption} from '@app/run-planning/model/option';

export enum FormFieldType {
  SECTION = 'section',
  TEXT = 'text',
  TEXTBOX = 'textbox',
  SELECT = 'select',
  FILESELECT = 'fileSelect',
  NUMBER = 'number',
  RADIO = 'radio',
  INTEGER = 'integer',
  CHECKBOX = 'checkbox',
  GENOME = 'genome',
  REFERENCE_FILE = 'referenceFile',
  FIELD_GROUP = 'fieldgroup',
}

export interface IFormFieldSettings extends IReferenceFileFieldSettings, IGenomeFieldSettings {
  dependantFieldIds?: string[];
}

/* For IFormField with type === 'referenceFile' */
export interface IReferenceFileFieldSettings {
  referenceFileGenomeFilterFieldId?: string;
  referenceFileGenomeHashTableVersion?: string; // Not from GSS, computed on UI
  referenceFileTypeFilter?: string;
  systemTagFilters?: Array<{ [key: string]: string }>;
  isMultiSelect?: boolean; // Not from GSS, computed on UI
  maxSelection?: number;
  minSelection?: number;
}

/* For IFormField with type === 'genome' */
export interface IGenomeFieldSettings {
  hashTableVersion?: string;
}

export interface IFormFieldChoice {
  text: string;
  value: string;
  selected: boolean;
  availableWhen?: IFormFieldAvailableWhen[];
  disabled?: boolean;
  valid?: boolean;
}

export interface IFormFieldAvailableWhen {
  id: string;
  hasValue: string[];
}

// TODO value (for default value) is missing?
export interface IFormField {
  type: FormFieldType;
  id: string;
  label: string;
  mapping?: string;
  required?: boolean;
  hidden?: boolean;
  helpText?: string;
  choices?: IFormFieldChoice[];
  regex?: string;
  regexErrorMessage?: string;
  min?: number;
  max?: number;
  hasCustomOption?: boolean;
  groupedChoices?: GroupedOption[];
  disabled?: boolean;
  maxLength?: any;
  minLength?: any;
  updateRenderOnChange: boolean;
  settings: IFormFieldSettings;
  review?: string;
  genomeFilterGenomeId?: string; // Only for reference file fields with genome dependency
  genomeFilterGenomeDisplayName?: string; // Resolved display name for the genome filter
  resolvedSystemTagFilters?: Array<{ [key: string]: string }>; // Explicitly propagated from settings after AVD render
}

export interface IFieldGroupFormField extends IFormField {
  type: FormFieldType.FIELD_GROUP;
  fields: IFormField[];
  minFieldGroupInstances?: number;
  maxFieldGroupInstances?: number;
}

export function isFieldGroupFormField(field: IFormField): field is IFieldGroupFormField {
  return field.type === FormFieldType.FIELD_GROUP;
}

export function isMultiSelectField(field: IFormField): boolean {
  return field.type === FormFieldType.SELECT
    && field.settings != null
    && field.settings.maxSelection != null
    && field.settings.maxSelection > 1;
}

/**
 * Converts systemTagFilters from AVD field settings to the API systemTags query format.
 * Each filter object represents a single key:value pair. Multiple values for the same key
 * should be separate objects in the input array (the API applies OR for same key, AND across keys).
 * e.g. [{"fasta": "hg19"}, {"fasta": "hg38"}, {"isHumanGenome": "true"}] → ["fasta:hg19", "fasta:hg38", "isHumanGenome:true"]
 */
export function convertSystemTagFiltersToApiFormat(filters: Array<{ [key: string]: string }>): string[] {
  if (!filters || filters.length === 0) { return []; }
  var result: string[] = [];
  filters.forEach(function(filterObj) {
    Object.keys(filterObj).forEach(function(key) {
      var value = filterObj[key];
      if (value != null && value !== '') {
        result.push(key + ':' + value);
      }
    });
  });
  return result;
}
